import { CopyAllOutlined, SummarizeOutlined } from "@mui/icons-material";
import {
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { formatDate } from "date-fns";
import React, { useEffect, useState } from "react";
import ProgressBar from "../../../components/graphs/ProgressBar";
import InfoIcon from "../../../components/misc/InfoIcon";
import SortIcon from "../../../components/misc/SortIcon";
import { useError } from "../../../contexts/ErrorContext";
import Translations from "../../../lang/en/pages.json";
import {
  copyToClipboard,
  cutOffText,
  formatNumberWithSuffixPrefix,
} from "../../../utils";
import {
  CaseInformationResolutionType,
  caseResultColorMap,
  CaseResultType,
  TableColumn as Column,
  RelatedCaseRow,
  resolutionTypeColorMap,
  TableSortConfig as SortConfig,
  TableFilters,
} from "../types";


const resolutionTypeLabelMap: Record<CaseInformationResolutionType, string> = {
  settlement: "Settlement",
  trial: "Verdict",
  dismissal: "Dismissal",
  summary_judgment: "Summary Judgment",
};



const columns: Column<RelatedCaseRow>[] = [
  {
    id: "relatedCase",
    label:
      Translations.pages.dashboard_page.tabs.overview.similar_cases_table_block
        .columns.case_name.label,
    sortable: true,
    getValue: (row) => row.external_id,
    columnWidth: "20%",
  },
  {
    id: "summaryIcon",
    label: "",
    columnWidth: "3%",
  },
  {
    id: "similarityScore",
    label:
      Translations.pages.dashboard_page.tabs.overview.similar_cases_table_block
        .columns.similarity_score.label,
    sortable: true,
    getValue: (row) => row.similarity_score,
    columnWidth: "5%",
    backgroundColor: "rgba(0, 121, 107, 0.04)",
    textAlign: "center",
  },
  {
    id: "resolutionType",
    label:
      Translations.pages.dashboard_page.tabs.overview.similar_cases_table_block
        .columns.resolution_type.label,
    sortable: true,
    getValue: (row) => row.resolution_type,
    textAlign: "center",
  },
  {
    id: "caseResult",
    label:
      Translations.pages.dashboard_page.tabs.overview.similar_cases_table_block
        .columns.case_result.label,
    getValue: (row) => row.case_result,
    sortable: true,
    textAlign: "center",
  },
  {
    id: "jurisdiction",
    label:
      Translations.pages.dashboard_page.tabs.overview.similar_cases_table_block
        .columns.jurisdiction.label,
    getValue: (row) => row.jurisdiction,
    sortable: true,
  },
  {
    id: "resolvedDamages",
    label:
      Translations.pages.dashboard_page.tabs.overview.similar_cases_table_block
        .columns.resolved_damages.label,
    sortable: true,
    getValue: (row) => {
      return row.damages_amount
        ? formatNumberWithSuffixPrefix(row.damages_amount, "$", true)
        : null;
    },
    // getValue: (row) => `$${formatNumberWithSuffix(row.damages_amount, true)}`,
    textAlign: "center",
  },
  // {
  //   id: "timeToResolution",
  //   label:
  //     Translations.pages.dashboard_page.tabs.overview.similar_cases_table_block
  //       .columns.time_to_resolution.label,
  //   getValue: (row) => row.time_to_resolution,
  //   sortable: true,
  // },
];

interface RelatedCasesTableProps {
  data: RelatedCaseRow[];
  onSimilarityScoreClick: (row: any) => void;
  onSimilaritySummaryClick: (row: any) => void;
  filters: TableFilters;
  id: string;
}

const RelatedCasesTable: React.FC<RelatedCasesTableProps> = ({
  data,
  onSimilarityScoreClick,
  onSimilaritySummaryClick,
  filters: { resolutionType, caseResult },
  id,
}) => {
  const { showSuccess, showError } = useError();
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "similarityScore",
    direction: "desc",
  });

  const [visibleData, setVisibleData] = useState<RelatedCaseRow[]>(data);

  const handleSort = (columnId: string) => {
    setSortConfig((prevConfig) => ({
      key: columnId,
      direction:
        prevConfig.key === columnId && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  useEffect(() => {
    const filteredData = data.filter(
      (row) =>
        (!resolutionType || row.resolution_type === resolutionType) &&
        (!caseResult || row.case_result === caseResult)
    );
    setVisibleData(getSortedData(filteredData));
  }, [data, resolutionType, caseResult, sortConfig]);

  const handleCopy = (id: string) => {
    try {
      copyToClipboard(id);
      showSuccess(
        Translations.pages.dashboard_page.tabs.overview.messages.case_id_copied
      );
    } catch (e) {
      console.error(e);
      showError(
        Translations.pages.dashboard_page.tabs.overview.errors.case_id_copy
      );
    }
  };

  const getSortedData = (data: RelatedCaseRow[]) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const column = columns.find((col) => col.id === sortConfig.key);
      const getValue =
        column?.getValue ||
        ((row: RelatedCaseRow) => row[sortConfig.key as keyof RelatedCaseRow]);

      const aValue = getValue(a);
      const bValue = getValue(b);

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  const renderSortableHeader = (column: Column<RelatedCaseRow>) => {
    if (!column.sortable) {
      return column.label;
    }

    return (
      <TableSortLabel
        hideSortIcon={false}
        active={sortConfig.key === column.id}
        direction={sortConfig.key === column.id ? sortConfig.direction : "asc"}
        onClick={() => handleSort(column.id)}
        IconComponent={() => {
          if (sortConfig.key === column.id) {
            return sortConfig.direction === "asc" ? (
              <SortIcon upColor="#333333" downColor="#00000066" />
            ) : (
              <SortIcon upColor="#00000066" downColor="#333333" />
            );
          } else {
            return <SortIcon upColor="#00000066" downColor="#00000066" />;
          }
        }}
      >
        <Typography variant="caption" color="#475467">
          {column.label}
        </Typography>
      </TableSortLabel>
    );
  };

  const renderCell = (row: RelatedCaseRow, columnId: string) => {
    switch (columnId) {
      case "relatedCase":
        return (
          <TableCell width={"20%"}>
            <Stack spacing={0.5}>
              <Typography variant="body2" color="#101828">
                {row.name}
              </Typography>

              <Stack
                height={20}
                sx={{
                  padding: 0.5,
                  borderRadius: 1,
                  backgroundColor: "rgba(0, 0, 0, 0.12)",
                }}
                alignItems={"center"}
                direction="row"
                justifyContent="space-between"
              >
                <Typography variant="body2" color="rgba(0, 0, 0, 0.87)">
                  {cutOffText(row.external_id ?? "N/A", 25, "end")}
                </Typography>
                {row.external_id && row.external_id !== "N/A" && (
                  <IconButton onClick={() => handleCopy(row.external_id ?? "")}>
                    <CopyAllOutlined sx={{ fontSize: "1rem" }} />
                  </IconButton>
                )}
              </Stack>
            </Stack>
          </TableCell>
        );
      case "summaryIcon":
        return (
          <TableCell width={"3%"} sx={{ padding: 0 }}>
            <IconButton onClick={() => onSimilaritySummaryClick(row)}>
              <SummarizeOutlined />
            </IconButton>
          </TableCell>
        );
      case "similarityScore":
        return (
          <TableCell
            width={"5%"}
            sx={{
              backgroundColor: "rgba(0, 121, 107, 0.04)",
            }}
          >
            <Stack spacing={2} direction="row" alignItems="center">
              <ProgressBar
                filledColor={"#7C4DFF"}
                progress={
                  typeof row.similarity_score === "number"
                    ? row.similarity_score
                    : null
                }
                progressPlacement="inside"
                emptyColor="#E0E0E0"
              />
              {typeof row.similarity_score === "number" && (
                <InfoIcon
                  fontSize={24}
                  backgroundColor="#5E35B1"
                  onClick={() => onSimilarityScoreClick(row)}
                />
              )}
            </Stack>
          </TableCell>
        );
      case "resolutionType":
        return (
          <TableCell sx={{ textAlign: "center" }}>
            <Chip
              label={
                resolutionTypeLabelMap[
                  row.resolution_type as CaseInformationResolutionType
                ] ?? row.resolution_type
              }
              style={{
                backgroundColor:
                  resolutionTypeColorMap[
                    row.resolution_type as CaseInformationResolutionType
                  ].bg,
                color:
                  resolutionTypeColorMap[
                    row.resolution_type as CaseInformationResolutionType
                  ].text,
                borderRadius: 15,
              }}
            />
          </TableCell>
        );

      case "jurisdiction":
        return (
          <TableCell width={"20%"}>
            <Stack spacing={0.5}>
              <Typography variant="body2" color="#475467">
                {row.jurisdiction}
              </Typography>
            </Stack>
          </TableCell>
        );
      case "caseResult":
        return (
          <TableCell sx={{ textAlign: "center" }}>
            <Chip
              label={row.case_result}
              style={{
                textTransform: "capitalize",
                backgroundColor:
                  caseResultColorMap[row.case_result as CaseResultType].bg,
                color:
                  caseResultColorMap[row.case_result as CaseResultType].text,
                borderRadius: 15,
              }}
            />
          </TableCell>
        );
      case "resolvedDamages":
        return (
          <TableCell sx={{ textAlign: "center" }}>
            {row.damages_amount
              ? formatNumberWithSuffixPrefix(row.damages_amount, "$", true)
              : null}
          </TableCell>
        );
      case "timeToResolution":
        return (
          <TableCell>
            <Stack spacing={1}>
              <Typography variant="body2">{row.time_to_resolution}</Typography>
              <Stack
                direction={"row"}
                spacing={0.5}
                alignItems={"center"}
                borderRadius={1}
                px={1}
                py={0.5}
                sx={{ backgroundColor: "rgba(0, 0, 0, 0.12)" }}
              >
                <Typography variant="body2" color="#475467">
                  {
                    Translations.pages.dashboard_page.tabs.overview
                      .similar_cases_table_block.columns.filing_date
                      .value_prefix
                  }
                </Typography>
                <Typography fontWeight={"bold"} variant="body2" color="#475467">
                  {row.filing_year ? formatDate(row.filing_year, "yyyy") : null}
                </Typography>
              </Stack>
            </Stack>
          </TableCell>
        );
      default:
        return (
          <TableCell>
            {row[columnId as keyof RelatedCaseRow] as string}
          </TableCell>
        );
    }
  };

  return (
    <TableContainer id={id}>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                width={column.columnWidth}
                sx={{
                  textAlign: column.textAlign ?? "left",
                  backgroundColor: column.backgroundColor,
                }}
              >
                {renderSortableHeader(column)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleData.map((row, index) => (
            <TableRow key={index}>
              {columns.map((column) => (
                <React.Fragment key={column.id}>
                  {renderCell(row, column.id)}
                </React.Fragment>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RelatedCasesTable;
